import React from "react";
import ReactDOM from "react-dom";
import { transparentize } from "polished";
import styled, { css } from "styled-components";

import { useEventListener } from "src/library/hooks";
import { media } from "src/library/utils";

import { Button } from "../Button";
import { Text } from "../Text/Text";

export interface IActionsPopOutProps {
  steps: IStep[];
  action: () => void;
  handleClose: () => void;
  directionHorizontal: "left" | "right";
  directionVertical: "top" | "bottom";
  position?: { x: number; y: number };
  disableAction?: boolean;
  hideCaret?: boolean;
  className?: string;
}
export interface IStep {
  title?: string;
  hideFooter?: boolean;
  component: JSX.Element;
  padComponent?: boolean;
  allowNext: boolean;
}

const ActionsPopOut: React.ForwardRefRenderFunction<HTMLDivElement, IActionsPopOutProps> = (props, ref) => {
  const [ stepIndex, setStepIndex ] = React.useState(0);
  const popoutRef = React.useRef<HTMLDivElement>(null);

  const clickHandler = React.useCallback(
    (event: MouseEvent) => {
      if (popoutRef.current && popoutRef.current.contains && !popoutRef.current.contains(event.target as Node)) {
        props.handleClose();
      }
    },
    [ popoutRef ]
  );

  useEventListener("mousedown", clickHandler, document);

  const PopOutContent = (
    <>
      {props.steps
        .filter((_step, i) => i < stepIndex)
        .map((step, i) => {
          return (
            <>
              {!!step.title && (
                <StepHeader
                  className="clickable"
                  onClick={() => setStepIndex(i)}
                  key={i}
                >
                  {props.steps.length > 1 && (
                    <Number weight="bold">
                      {i + 1}
                      .
                    </Number>
                  )}

                  <Text fontSize="h4">
                    {step.title}
                  </Text>
                </StepHeader>
              )}
            </>
          );
        })}

      {props.steps.map((step, i) => {
        if (stepIndex === i) {
          return (
            <StepWrapper key={i}>
              {!!step.title && (
                <StepHeader>
                  {props.steps.length > 1 && (
                    <Number weight="bold">
                      {i + 1}
                      .
                    </Number>
                  )}

                  <Text fontSize="h4">
                    {step.title}
                  </Text>
                </StepHeader>
              )}

              <ComponentWrapper className={step.padComponent ? "padded" : "fluid"}>
                {step.component}
              </ComponentWrapper>

              {step.hideFooter !== true && props.steps.length > 1 && stepIndex !== props.steps.length - 1 && (
                <Actions>
                  <Button onClick={() => props.handleClose()}>
                    Cancel
                  </Button>

                  <Button
                    background="primaryDark"
                    disabled={stepIndex === props.steps.length - 1 || !step.allowNext}
                    onClick={() => setStepIndex(stepIndex + 1)}
                  >
                    Next
                  </Button>
                </Actions>
              )}
            </StepWrapper>
          );
        }

        return null;
      })}

      {props.steps[ stepIndex ] && props.steps[ stepIndex ].hideFooter !== true && stepIndex === props.steps.length - 1 && (
        <Actions>
          <Button onClick={() => props.handleClose()}>
            Cancel
          </Button>

          <Button
            disabled={props.disableAction !== undefined ? props.disableAction : props.steps[ stepIndex ].allowNext === false}
            onClick={() => {
              props.action();
              props.handleClose();
            }}
          >
            Confirm
          </Button>
        </Actions>
      )}
    </>
  );

  return (
    <div ref={ref}>
      {props.position ? (
        ReactDOM.createPortal(
          <PopOutWrapper
            ref={popoutRef}
            className={`pull-${props.directionHorizontal} push-${props.directionVertical} ${props.className ?? ""}`}
            {...props.position}
          >
            {PopOutContent}
          </PopOutWrapper>,
          document.body
        )
      ) : (
        <PopOutWrapper
          ref={popoutRef}
          className={`pull-${props.directionHorizontal} push-${props.directionVertical}`}
        >
          {PopOutContent}
        </PopOutWrapper>
      )}
    </div>
  );
};

const Number = styled(Text)`
  padding-right: 10px;
`;

export const PopOutWrapper = styled.div<{ x?: number; y?: number; hideCaret?: boolean }>`
  ${({
    theme, y, x, hideCaret
  }) => css`
    ${typeof x !== "number" || typeof y !== "number" ?
    css`
          top: 100%;

          &.orders-popout {
            max-width: 300px;
          }

          &.pull-left {
            left: 0;
            &:before {
              left: 10px;
            }
          }
          &.pull-right {
            right: 0;

            &:before {
              right: 10px;
            }
          }
          &.push-top {
            &:before {
              top: -10px;
              border-bottom: 10px solid white;
            }
          }
          &.push-bottom {
            ${media({ gt: theme.breakpoints.sm })} {
              /* this is only for schedule date picket */
              right: 20px;
            }
            &:before {
              bottom: -10px;
              border-top: 10px solid white;
            }
          }
        ` :
    css`
          top: ${y}px;

          &.orders-popout {
            max-width: 300px;
          }
          
          &.pull-left {
            left: ${x}px;
            &:before {
              left: 10px;
            }
          }
          &.pull-right {
            right: 0;

            &:before {
              right: 10px;
            }
          }
          &.push-top {
            &:before {
              top: -10px;
              border-bottom: 10px solid white;
            }
          }
          &.push-bottom {
            ${media({ gt: theme.breakpoints.sm })} {
              right: 20px;
            }
            &:before {
              bottom: -10px;
              border-top: 10px solid white;
            }
          }
        `}

    display: flex;
    flex-direction: column;

    &:before {
      content: "";
      display: ${hideCaret ? "none" : "block"}
      position: absolute;
      border-left: 10px solid transparent;
      width: 0px;
      border-right: 10px solid transparent;

      ${media({ lt: theme.breakpoints.sm })} {
        border-bottom: 10px solid transparent;
      }
    }
    position: absolute;
    z-index: 150;
    min-width: 300px;
    margin: 10px;
    background: white;

    box-shadow: 0 0 6px ${transparentize(0.9, theme.colors.darkGrey)};

    ${media({ lt: theme.breakpoints.sm })} {
      width: 95%;
    }
  `}
`;
const StepWrapper = styled.div``;

const StepHeader = styled.div`
  ${({ theme }) => css`
    &.clickable {
      cursor: pointer;
      border-bottom: 1px solid ${theme.colors.lightGrey};
    }

    padding: ${theme.spacing.padding.md};
  `}
`;

const Actions = styled.div`
  ${({ theme }) => css`
    display: flex;
    padding: ${theme.spacing.padding.md};

    * {
      width: 100%;
      margin: 0 ${theme.spacing.margins.sm};
    }
  `}
`;

const ComponentWrapper = styled.div`
  &.padded {
    ${({ theme }) => css`
      padding: ${theme.spacing.padding.md};
    `}
  }
`;

export default React.forwardRef(ActionsPopOut);
